import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { isEmbeddedWebView } from '../../utils/inAppBrowser';

/**
 * The pitch, for the one moment in the funnel when a parent is simply
 * waiting: a banner of three slides on the character-generating step of both
 * web2app funnels, above the "watch while you wait" strip.
 *
 *   1. Characters made from anything — a drawing, a stuffed animal, a photo.
 *   2. Stories those characters star in, with the parent (or the child)
 *      shaping the storyline.
 *   3. Storybooks that help them read, and read aloud when they'd rather listen.
 *   4. Pixley Learn: letters, counting and a daily wonder, as play.
 *
 * The slides advance by themselves every few seconds, with the time to the
 * next one drawn into the active dot — and it's that dot's fill animation
 * that moves things on when it completes, so a pause (a finger held on the
 * banner, a hover, a hidden tab) resumes exactly where it left off. A swipe
 * or a tap on a dot moves between slides by hand. All three slides stay
 * mounted and cross-fade, so the art is loaded before it's needed and
 * nothing pops in.
 *
 * Slide two plays a short, muted, heavily compressed loop of a real story
 * (well under a megabyte, so it doesn't crowd the generation request that's
 * in flight on this connection); the other two are stills.
 *
 * Its own `fwp-` class namespace and inline <style>, like FunnelWaitVideo,
 * so neither funnel's `fa-`/`fi-` styles reach in.
 */

export const SLIDE_MS = 6000;
const SWIPE_PX = 36;

export const SLIDES = [
  {
    key: 'characters',
    eyebrow: 'Their characters',
    chip: { label: 'Made from', text: 'Maya’s drawing from school' },
    title: 'Turn anything they love into a character',
    body: 'A crayon drawing, a stuffed animal, a photo of your child. Pixley gives it a voice and a personality all its own.',
    art: '/assets/funnel/maya-drawing-to-life.webp',
    artPosition: '46% 50%',
    theme: 'violet',
  },
  {
    key: 'stories',
    eyebrow: 'Stories they star in',
    title: 'Their character, in a story you shape',
    body: 'Pick the lesson or the adventure, or let them take the wheel, then watch it play out as a cartoon together.',
    // A real Pixley story starring Maya, looping muted behind the copy
    art: '/assets/funnel/maya-story-poster.webp',
    artPosition: '50% 50%',
    video: '/assets/funnel/maya-story-loop',
    chip: { label: 'Story you picked', text: 'Maya learns to share' },
    theme: 'blue',
  },
  {
    key: 'books',
    eyebrow: 'Storybooks they’ll actually read',
    title: 'Books that grow little readers',
    body: 'Illustrated pages at their reading level. They read along when they want to, and listen when they’d rather be read to.',
    // Two pages of Maya's own storybook
    art: '/assets/funnel/maya-storybook-spread.webp',
    artPosition: '50% 22%',
    readAloud: true,
    theme: 'green',
  },
  {
    key: 'learn',
    eyebrow: 'Pixley Learn',
    title: 'Lessons that feel like play',
    body: 'Letters, counting, and writing, taught by the characters they already love. A few minutes a day that actually stick.',
    art: '/assets/funnel/pixley-learn.webp',
    artPosition: '50% 35%',
    theme: 'teal',
  },
];

// Worth warming while they pick a friend, so the banner is whole the moment
// the wait screen mounts
export const WHY_PIXLEY_ART = SLIDES.map((s) => s.art);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const EASE = [0.22, 1, 0.36, 1];

const SoundWave = () => (
  <span className="fwp-wave" aria-hidden="true">
    <i /><i /><i /><i />
  </span>
);

const FunnelWhyPixley = () => {
  const [active, setActive] = useState(0);
  const [held, setHeld] = useState(false);
  const [hidden, setHidden] = useState(false);
  const reduced = useRef(prefersReducedMotion());
  const pointer = useRef(null);
  const paused = held || hidden;

  const go = useCallback((index) => {
    setActive(((index % SLIDES.length) + SLIDES.length) % SLIDES.length);
  }, []);

  // A background tab is no one to pitch to; pick up where it left off
  useEffect(() => {
    const onVisibility = () => setHidden(document.visibilityState === 'hidden');
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Swipe — a finger (or mouse) that travels sideways far enough moves a
  // slide; while it's down the slide holds. Touch is handled through the
  // touch events, mouse and pen through pointer events: a phone browser
  // can drop the pointerup when it decides a touch is a scroll, and the
  // touch events still deliver the finger's end position.
  const begin = (x, y) => {
    pointer.current = { x, y };
    setHeld(true);
  };
  const end = (x, y) => {
    const start = pointer.current;
    pointer.current = null;
    setHeld(false);
    if (!start) return;
    const dx = x - start.x;
    const dy = y - start.y;
    if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? active + 1 : active - 1);
  };
  const cancel = () => {
    pointer.current = null;
    setHeld(false);
  };
  const onPointerDown = (e) => {
    if (e.pointerType !== 'touch') begin(e.clientX, e.clientY);
  };
  const onPointerUp = (e) => {
    if (e.pointerType !== 'touch') end(e.clientX, e.clientY);
  };
  const onPointerCancel = (e) => {
    if (e.pointerType !== 'touch') cancel();
  };
  const onTouchStart = (e) => {
    const t = e.touches[0];
    if (t) begin(t.clientX, t.clientY);
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    if (t) end(t.clientX, t.clientY);
    else cancel();
  };

  const transition = reduced.current ? { duration: 0 } : { duration: 0.55, ease: EASE };

  return (
    <section
      className="fwp"
      aria-roledescription="carousel"
      aria-label="What Pixley does"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerLeave={onPointerCancel}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={cancel}
    >
      <FunnelWhyPixleyStyles />

      <div className="fwp-stage">
        {SLIDES.map((slide, i) => {
          const on = i === active;
          return (
            <motion.article
              key={slide.key}
              className={`fwp-slide fwp-theme-${slide.theme}`}
              aria-hidden={!on}
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${SLIDES.length}`}
              initial={false}
              animate={{ opacity: on ? 1 : 0, x: on ? 0 : i < active ? -28 : 28 }}
              transition={transition}
              style={{ pointerEvents: on ? 'auto' : 'none' }}
            >
              <div className="fwp-art">
                <img src={slide.art} alt="" style={{ objectPosition: slide.artPosition }} draggable="false" />
                {/* TikTok's browser paints player chrome over any <video>: the
                    animated-WebP version (scripts/animated-webp.py) goes there instead */}
                {slide.video && isEmbeddedWebView() && (
                  <img className="fwp-video" src={`${slide.video}.webp`} alt="" aria-hidden="true" draggable="false" />
                )}
                {slide.video && !isEmbeddedWebView() && (
                  <video
                    className="fwp-video"
                    poster={slide.art}
                    autoPlay
                    muted
                    loop
                    playsInline
                    webkit-playsinline=""
                    preload="auto"
                    aria-hidden="true"
                  >
                    <source src={`${slide.video}.webm`} type="video/webm" />
                    <source src={`${slide.video}.mp4`} type="video/mp4" />
                  </video>
                )}
                <div className="fwp-art-fade" />
                {slide.chip && (
                  <motion.div
                    className="fwp-prompt"
                    initial={false}
                    animate={{ opacity: on ? 1 : 0, y: on ? 0 : 10 }}
                    transition={{ ...transition, delay: on && !reduced.current ? 0.25 : 0 }}
                  >
                    <span className="fwp-prompt-label">{slide.chip.label}</span>
                    <span className="fwp-prompt-text">{slide.chip.text}</span>
                  </motion.div>
                )}
              </div>

              <div className="fwp-copy">
                {slide.readAloud && (
                  <motion.div
                    className="fwp-readaloud"
                    initial={false}
                    animate={{ opacity: on ? 1 : 0, y: on ? 0 : 10 }}
                    transition={{ ...transition, delay: on && !reduced.current ? 0.25 : 0 }}
                  >
                    <SoundWave />
                    <span>Reading aloud…</span>
                  </motion.div>
                )}
                <small className="fwp-eyebrow">{slide.eyebrow}</small>
                <h2 className="fwp-title">{slide.title}</h2>
                <p className="fwp-body">{slide.body}</p>
              </div>
            </motion.article>
          );
        })}
      </div>

      <div className="fwp-dots" role="tablist" aria-label="Slides">
        {SLIDES.map((slide, i) => (
          <button
            key={slide.key}
            type="button"
            role="tab"
            aria-selected={i === active}
            aria-label={`${slide.eyebrow}, slide ${i + 1} of ${SLIDES.length}`}
            className={`fwp-dot${i === active ? ' fwp-dot-active' : ''}`}
            onClick={() => go(i)}
            // A tap on a dot is not a swipe
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {i === active && (
              <span
                // Remounts with every slide, so the fill restarts from empty
                key={active}
                className="fwp-dot-fill"
                style={{
                  animationDuration: `${SLIDE_MS}ms`,
                  animationPlayState: paused ? 'paused' : 'running',
                }}
                onAnimationEnd={() => go(active + 1)}
              />
            )}
          </button>
        ))}
      </div>
    </section>
  );
};

// The banner runs nearly to the card's edges; both funnels' cards share the
// same padding (26px, 18px on phones), which the negative margins undo.
const FunnelWhyPixleyStyles = () => (
  <style>{`
    .fwp {
      margin: 24px -12px 0;
      touch-action: pan-y;
      user-select: none;
      -webkit-user-select: none;
      -webkit-tap-highlight-color: transparent;
    }

    .fwp-stage {
      position: relative;
      height: 338px;
      border-radius: 22px;
      overflow: hidden;
      box-shadow: 0 14px 34px rgba(11,26,51,0.18), 0 2px 6px rgba(11,26,51,0.08);
      background: #1B2A5A;
    }
    .fwp-slide {
      position: absolute; inset: 0;
      display: flex; flex-direction: column;
      color: #FFF;
    }
    .fwp-theme-violet { background: linear-gradient(180deg, #2E1E7A 0%, #4A30B8 100%); }
    .fwp-theme-blue   { background: linear-gradient(180deg, #0F3A66 0%, #1E63A8 100%); }
    .fwp-theme-green  { background: linear-gradient(180deg, #14503A 0%, #1F7A57 100%); }
    .fwp-theme-teal   { background: linear-gradient(180deg, #0C3F52 0%, #176A86 100%); }

    /* The art fills the whole slide; the copy sits on a scrim over its foot */
    .fwp-art {
      position: absolute; inset: 0;
      overflow: hidden;
    }
    .fwp-art img, .fwp-video {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      display: block;
    }
    .fwp-art-fade {
      position: absolute; inset: 0;
      background: linear-gradient(180deg, rgba(0,0,0,0) 34%, var(--fwp-fade-mid) 62%, var(--fwp-fade) 100%);
    }
    .fwp-theme-violet { --fwp-fade: rgba(30,16,70,0.96); --fwp-fade-mid: rgba(30,16,70,0.55); }
    .fwp-theme-blue   { --fwp-fade: rgba(8,34,64,0.96);  --fwp-fade-mid: rgba(8,34,64,0.55); }
    .fwp-theme-green  { --fwp-fade: rgba(10,46,32,0.96); --fwp-fade-mid: rgba(10,46,32,0.55); }
    .fwp-theme-teal   { --fwp-fade: rgba(7,40,54,0.96);  --fwp-fade-mid: rgba(7,40,54,0.55); }

    /* The chip floating over the art: what this slide was made from */
    .fwp-prompt {
      position: absolute; left: 14px; top: 14px;
      max-width: calc(100% - 28px);
      display: flex; flex-direction: column; gap: 1px;
      padding: 8px 12px 9px;
      border-radius: 14px 14px 14px 4px;
      background: rgba(255,255,255,0.94);
      color: #0B1A38;
      box-shadow: 0 8px 22px rgba(0,0,0,0.28);
    }
    .fwp-prompt-label {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 9.5px; font-weight: 700;
      letter-spacing: 0.1em; text-transform: uppercase;
      color: #0A6CFF;
    }
    .fwp-prompt-text {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 14px; font-weight: 600;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }

    /* Slide three: the narration badge */
    .fwp-readaloud {
      align-self: flex-start;
      margin-bottom: 10px;
      display: flex; align-items: center; gap: 8px;
      padding: 6px 12px 6px 10px;
      border-radius: 999px;
      background: rgba(255,255,255,0.94);
      color: #0B1A38;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 13px; font-weight: 600;
      box-shadow: 0 8px 22px rgba(0,0,0,0.28);
    }
    .fwp-wave { display: flex; align-items: center; gap: 2.5px; height: 14px; }
    .fwp-wave i {
      display: block; width: 3px; border-radius: 2px;
      background: #0A6CFF;
      animation: fwpWave 1s ease-in-out infinite;
    }
    .fwp-wave i:nth-child(1) { height: 6px; animation-delay: 0s; }
    .fwp-wave i:nth-child(2) { height: 14px; animation-delay: 0.15s; }
    .fwp-wave i:nth-child(3) { height: 9px; animation-delay: 0.3s; }
    .fwp-wave i:nth-child(4) { height: 12px; animation-delay: 0.45s; }
    @keyframes fwpWave {
      0%, 100% { transform: scaleY(0.45); }
      50% { transform: scaleY(1); }
    }

    .fwp-copy {
      position: absolute; left: 0; right: 0; bottom: 0;
      display: flex; flex-direction: column;
      padding: 0 18px 16px;
      text-align: left;
    }
    .fwp-eyebrow {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 11px; font-weight: 700;
      letter-spacing: 0.1em; text-transform: uppercase;
      color: rgba(255,255,255,0.72);
      margin-bottom: 4px;
    }
    .fwp-title {
      margin: 0 0 6px;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 22px; font-weight: 700;
      line-height: 1.15;
      letter-spacing: -0.01em;
      color: #FFF;
      text-wrap: balance;
      text-shadow: 0 1px 2px rgba(0,0,0,0.35);
    }
    .fwp-body {
      margin: 0;
      font-size: 14px; line-height: 1.42;
      color: rgba(255,255,255,0.9);
      text-shadow: 0 1px 2px rgba(0,0,0,0.35);
    }

    /* The dots: the active one lengthens and fills with the time to the next slide */
    .fwp-dots {
      display: flex; justify-content: center; align-items: center; gap: 6px;
      margin-top: 12px;
    }
    .fwp-dot {
      position: relative;
      width: 8px; height: 8px;
      padding: 0;
      border: none; border-radius: 999px;
      background: #D5DEEB;
      cursor: pointer;
      overflow: hidden;
      transition: width 320ms cubic-bezier(.2,.8,.2,1), background 200ms;
    }
    /* A bigger finger target than the dot itself */
    .fwp-dot::before { content: ''; position: absolute; inset: -10px; }
    .fwp-dot-active { width: 34px; background: #D5DEEB; }
    .fwp-dot-fill {
      position: absolute; left: 0; top: 0; bottom: 0;
      width: 100%;
      border-radius: 999px;
      background: linear-gradient(90deg, #296FFB, #3E8DFD);
      transform-origin: left center;
      animation-name: fwpFill;
      animation-timing-function: linear;
      animation-fill-mode: forwards;
    }
    @keyframes fwpFill {
      from { transform: scaleX(0); }
      to { transform: scaleX(1); }
    }
    .fwp-dot:focus-visible {
      outline: 3px solid rgba(10,108,255,0.4);
      outline-offset: 2px;
    }

    @media (max-width: 480px) {
      .fwp { margin: 20px -8px 0; }
      .fwp-stage { height: 296px; border-radius: 20px; }
      .fwp-copy { padding: 0 16px 14px; }
      .fwp-title { font-size: 19.5px; }
      .fwp-body { font-size: 13px; }
    }
    @media (max-width: 374px) {
      .fwp-stage { height: 306px; }
    }
    @media (max-height: 700px) {
      .fwp { margin-top: 16px; }
      .fwp-dots { margin-top: 10px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .fwp-wave i { animation: none; transform: none; }
      .fwp-dot { transition: none; }
    }
  `}</style>
);

export default FunnelWhyPixley;
